import LogCenter, {
	CDP,
	CDPSender,
	GateStrategy,
	LocalStorage,
} from '@deezer/logcenter';

import {
	buildClickProperties,
	buildScreenViewProperties,
	SPOT_AI_CLICKED_EVENT,
	SPOT_AI_EVENT_VERSION,
	SPOT_AI_SCREEN_VIEW_EVENT,
	type SpotAiAnalyticsIdentifiers,
	type SpotAiClickAction,
	type SpotAiScreenViewName,
} from '@modules/analytics/events';

type SegmentAnalyticsLike = {
	setAnonymousId?: (id: string) => unknown;
	page?: (...args: unknown[]) => unknown;
	__spotAiPageTitlePatch?: boolean;
};

declare global {
	interface Window {
		analytics?: SegmentAnalyticsLike;
	}
}

type SpotAiAnalyticsPayload = Readonly<{
	type: typeof SPOT_AI_CDP_LOG_TYPE;
	eventName: typeof SPOT_AI_SCREEN_VIEW_EVENT | typeof SPOT_AI_CLICKED_EVENT;
	eventVersion: typeof SPOT_AI_EVENT_VERSION;
	customAttributes: ReturnType<
		typeof buildScreenViewProperties | typeof buildClickProperties
	>;
}>;

type TrackScreenViewInput = SpotAiAnalyticsIdentifiers & {
	screenViewName: SpotAiScreenViewName;
	score?: number | null;
	pageTitle?: string;
};

type TrackClickInput = TrackScreenViewInput & {
	action: SpotAiClickAction;
};

const logCdpError = (type: string, error: unknown) => {
	if (process.env.NODE_ENV === 'test') return;
	// eslint-disable-next-line no-console -- analytics initialization errors must stay visible in dev/prod.
	console.error(`[analytics] ${type}`, error);
};

const SPOT_AI_CDP_LOG_TYPE = 'cdp';
const LOGCENTER_STORAGE_PREFIX = 'deezer:spot-ai:analytics:';
const pageTitlesByScreen = new Map<SpotAiScreenViewName, string>();
const cdpConfiguration = {
	handledLogTypes: [SPOT_AI_CDP_LOG_TYPE],
};

function patchSegmentPageTitleContext(): void {
	const analytics = window.analytics;
	if (!analytics?.page || analytics.__spotAiPageTitlePatch) return;

	const originalPage = analytics.page.bind(analytics);
	analytics.page = (...args: unknown[]) => {
		const properties =
			args[1] && typeof args[1] === 'object'
				? (args[1] as {screen_view_name?: unknown})
				: null;
		const screenViewName =
			typeof properties?.screen_view_name === 'string'
				? (properties.screen_view_name as SpotAiScreenViewName)
				: null;
		const pageTitle = screenViewName
			? pageTitlesByScreen.get(screenViewName)
			: null;

		if (pageTitle) {
			const options =
				args[2] && typeof args[2] === 'object'
					? (args[2] as Record<string, unknown>)
					: {};
			const context =
				options.context && typeof options.context === 'object'
					? (options.context as Record<string, unknown>)
					: {};
			const page =
				context.page && typeof context.page === 'object'
					? (context.page as Record<string, unknown>)
					: {};

			args[2] = {
				...options,
				context: {
					...context,
					page: {
						...page,
						title: pageTitle,
					},
				},
			};
		}

		return originalPage(...args);
	};
	analytics.__spotAiPageTitlePatch = true;
}

export class SpotAiAnalyticsClient {
	private readonly cdp: CDP;
	private readonly cdpGateStrategy: GateStrategy;
	private readonly logCenter: LogCenter;

	constructor(writeKey: string) {
		this.cdp = new CDP(writeKey, {
			filter: true,
			logger: logCdpError,
			minimumEventVersion: SPOT_AI_EVENT_VERSION,
			name: 'spot_ai',
			stack: 'web',
		});
		const localStorageManager = new LocalStorage({
			prefix: LOGCENTER_STORAGE_PREFIX,
		});
		const cdpSender = new CDPSender(this.cdp);

		this.cdpGateStrategy = new GateStrategy(
			cdpSender,
			localStorageManager,
			cdpConfiguration,
		);
		this.logCenter = new LogCenter([this.cdpGateStrategy], localStorageManager);
	}

	init(): void {
		this.cdp.init();
		patchSegmentPageTitleContext();
	}

	setAnonymousId(dzrUniqueId: string): void {
		window.analytics?.setAnonymousId?.(dzrUniqueId);
	}

	openConsentGate(): void {
		this.cdpGateStrategy.openGate();
	}

	async identify({
		dzrUniqueId,
		userId,
	}: SpotAiAnalyticsIdentifiers): Promise<void> {
		await this.cdp.identify({
			deezer_unique_id: dzrUniqueId,
			statistics: false,
			targetedAds: false,
			userId: userId ?? undefined,
		});
	}

	async trackScreenView(input: TrackScreenViewInput): Promise<void> {
		if (input.pageTitle) {
			pageTitlesByScreen.set(input.screenViewName, input.pageTitle);
		}
		await this.log({
			type: SPOT_AI_CDP_LOG_TYPE,
			eventName: SPOT_AI_SCREEN_VIEW_EVENT,
			eventVersion: SPOT_AI_EVENT_VERSION,
			customAttributes: buildScreenViewProperties(input),
		});
	}

	async trackClick(input: TrackClickInput): Promise<void> {
		await this.log({
			type: SPOT_AI_CDP_LOG_TYPE,
			eventName: SPOT_AI_CLICKED_EVENT,
			eventVersion: SPOT_AI_EVENT_VERSION,
			customAttributes: buildClickProperties(input),
		});
	}

	private async log(payload: SpotAiAnalyticsPayload): Promise<void> {
		await this.logCenter.log(payload);
	}
}
